import React from "react";

import {
  Box,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";

import { ReactComponent as CloseIcon } from "assets/images/icons/btn_close.svg";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";

const RedeemTokenModal = ({
  open,
  token,
  loading = false,
  errorMessage = "",
  onTokenChange,
  onClose,
  onValidate,
}) => {
  const normalizedToken = token?.trim() || "";
  const validateDisabled = !normalizedToken || loading;

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" || validateDisabled) {
      return;
    }

    event.preventDefault();
    onValidate?.();
  };

  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="redeem-token-title"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(31, 42, 68, 0.45)",
          },
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",

          width: "520px",
          maxWidth: "calc(100vw - 32px)",

          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          boxShadow: "0 18px 50px rgba(25, 35, 70, 0.28)",
          overflow: "hidden",
          outline: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            minHeight: "72px",
            px: "24px",
            py: "16px",
            boxSizing: "border-box",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            borderBottom: "1px solid #DDE2EB",
          }}
        >
          <Typography
            id="redeem-token-title"
            sx={{
              color: "#172B68",
              fontSize: "18px",
              fontWeight: 700,
            }}
          >
            Redeem a token
          </Typography>

          <IconButton
            onClick={onClose}
            disabled={loading}
            aria-label="Close redeem token modal"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            px: "24px",
            py: "24px",
          }}
        >
          <Typography
            sx={{
              mb: "16px",
              color: "#70798C",
              fontSize: "14px",
              lineHeight: 1.25,
            }}
          >
            Enter a support token a customer sent you out-of-band. A scope a
            colleague transfers to you arrives under{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 700,
              }}
            >
              Delegated to me
            </Box>{" "}
            automatically — no code needed.
          </Typography>

          <Typography
            component="label"
            htmlFor="support-token-input"
            sx={{
              display: "block",
              mb: "6px",
              color: "#20377C",
              fontSize: "13px",
              fontWeight: 700,
            }}
          >
            Support token (UUID) *
          </Typography>

          <TextField
            id="support-token-input"
            fullWidth
            autoFocus
            disabled={loading}
            value={token}
            error={Boolean(errorMessage)}
            helperText={errorMessage}
            onChange={(event) => {
              onTokenChange?.(event.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder="e.g. 3f2a...-...-..."
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "42px",
                borderRadius: "7px",
                bgcolor: "#FFFFFF",

                "& fieldset": {
                  borderColor: "#CBD2E1",
                },

                "&:hover fieldset": {
                  borderColor: "#AAB4C7",
                },

                "&.Mui-focused fieldset": {
                  borderWidth: "1px",
                  borderColor: "#20377C",
                },

                "&.Mui-error fieldset": {
                  borderColor: "#D92D20",
                },

                "&.Mui-error:hover fieldset": {
                  borderColor: "#D92D20",
                },

                "&.Mui-error.Mui-focused fieldset": {
                  borderColor: "#D92D20",
                },
              },

              "& .MuiInputBase-input": {
                px: "13px",
                py: 0,
                color: "#4F5665",
                fontSize: "14px",
              },

              "& input::placeholder": {
                color: "#8A94A8",
                opacity: 1,
              },

              "& .MuiFormHelperText-root": {
                mx: 0,
                mt: "5px",
                color: "#D92D20",
                fontSize: "12px",
              },
            }}
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            minHeight: "72px",
            px: "24px",
            py: "16px",
            boxSizing: "border-box",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            borderTop: "1px solid #DDE2EB",
            bgcolor: "#FAFBFD",
          }}
        >
          <OutlineButton
            width="80px"
            height="32px"
            disabled={loading}
            onClick={onClose}
          >
            Cancel
          </OutlineButton>
          <SolidButton
            disabled={validateDisabled}
            onClick={onValidate}
            width="160px"
            height="32px"
          >
            {loading ? "Validating..." : "Validate & preview"}
          </SolidButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default RedeemTokenModal;
